import React, { useEffect, useReducer, useRef, useState } from 'react';
import PreDeployConfirmModal from '../components/PreDeployConfirmModal';
import { RunAssistance } from '@back4app2/sdk';
import { Link, Outlet, useLocation, useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { App, AppStatus, AppStatusError, AuthenticationError, AuthorizationError, BlockedResourceError, CustomDomainStatus, DisposableEmailError, NetworkError, NotFoundError, PaymentPendingError, ServiceEnvironment, User, UserVerificationPendingError, DeploymentStatusError } from '@back4app2/sdk';
import { BACK4APP_DOT_COM_SITE_URL, BACK4APP_DOT_COM_DASHBOARD_URL } from '../settings';
import back4app2 from '../back4app2';
import LoadingSpinner from '../components/LoadingSpinner';
import MyAppStatus from '../components/MyAppStatus';
import { ReactComponent as LinkIconSVG } from '../assets/images/link-icon.svg';
import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import { ReactComponent as StatusSpinnerSVG } from '../assets/images/status-spinner.svg';
import { ReactComponent as StatusSuccessSVG } from '../assets/images/status-success.svg';
import ErrorMessage from '../components/ErrorMessage';
import MyAppActions, { canRunAppActions } from '../components/MyAppActions';
import { toast } from 'react-hot-toast';
import {
  DsButton, Eyebrow, ModalDialog, SettingsFooter, SettingsSaveProvider, StatusDot, StatusPill, SurfaceCard,
} from '../components/ds';
import type { SettingsSaveState } from '../components/ds';
import { ReactComponent as TriangleExclamationIcon } from '../assets/images/triangle-exclamation.svg';
import {
  ArrowLeftIcon, BranchIcon, ChevronRightIcon, DeploymentsIcon, GlobeIcon, LogsIcon, MetricsIcon,
  OverviewIcon, SettingsIcon, VariablesIcon,
} from '../components/ds/icons';
import { timeFromNow } from '../utils/dates';
import { getAppStatusSummary } from '../utils/appStatus';
import { TOAST_SURFACE } from '../components/ds/toast';
import { getVisibleSettingsTabs, upgradePath } from './MyAppSettings';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import { hasWritePermission } from '../utils/permissions';

interface MyAppState {
  isLoading: boolean;
  loadingErrorMessage?: string;
  app?: App;
  appLoadedAt?: Date;
  isDeletingApp: boolean;
}

/**
 * Runs the analysis, then decides what the Deploy button does.
 *
 * Wired only into the explicit Deploy actions, not into "save settings and
 * redeploy" or "domain changed, redeploy": someone who has just configured the
 * app does not need to be asked about its configuration, and a screen that
 * appears on every path is one nobody reads.
 */
const runPreDeploy = async (serviceEnvironmentId: string) => {
  try {
    return await back4app2.prepareDeployment(serviceEnvironmentId);
  } catch (e) {
    // Never block the deploy on the pre-check: it decides what to SHOW, and
    // the worker gate is what actually enforces. Failing here would turn an
    // analyzer problem into an inability to deploy.
    return { blocked: false, needsConfirmation: false };
  }
};

const INITIAL_STATE: MyAppState = {
  isLoading: true,
  isDeletingApp: false
};

enum MyAppActionType {
  RESET,
  FINISH_LOADING,
  SET_IS_DELETING_APP
}

const reset = () => ({
  type: MyAppActionType.RESET
} as const);

const finishLoading = (errorMessage?: string, app?: App) => ({
  type: MyAppActionType.FINISH_LOADING,
  payload: {
    errorMessage,
    app
  }
} as const);

const setIsDeletingApp = (isDeleting: boolean) => ({
  type: MyAppActionType.SET_IS_DELETING_APP,
  payload: isDeleting
} as const);

type MyAppAction = ReturnType<typeof reset> | ReturnType<typeof finishLoading> | ReturnType<typeof setIsDeletingApp>;

const reducer = (state: MyAppState = INITIAL_STATE, action: MyAppAction): MyAppState => {
  switch (action.type) {
    case MyAppActionType.RESET:
      return {
        ...INITIAL_STATE,
      };
    
    case MyAppActionType.FINISH_LOADING: {
      if (action.payload.errorMessage === 'App not found.' && state.isDeletingApp) {
        return {
          ...state,
          isLoading: false,
          appLoadedAt: new Date()
        }
      }
      return {
        ...state,
        isLoading: false,
        loadingErrorMessage: action.payload.errorMessage,
        app: action.payload.app,
        appLoadedAt: new Date()
      }
    }
    
    case MyAppActionType.SET_IS_DELETING_APP:
      return {
        ...state,
        isDeletingApp: action.payload
      }
  }
};

const MyApp = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  /**
   * Settings' own section menu, opened by the Settings tab itself.
   *
   * It lives up here because the trigger is in this component's tab bar and the
   * panel is down in MyAppSettings; the strip that used to carry the trigger
   * sat directly above the bar repeating the word it already said.
   */
  const [isSectionMenuOpen, setIsSectionMenuOpen] = useState(false);
  /** Published by whichever Settings form is mounted; drawn by the footer. */
  const [saveState, setSaveState] = useState<SettingsSaveState | null>(null);
  /** The section a confirmation is standing between the customer and. */
  const [pendingSettingsTab, setPendingSettingsTab] = useState<string | null>(null);

  const [pendingDeploy, setPendingDeploy] = useState<{
    serviceEnvironmentId: string;
    runAssistance: RunAssistance;
  } | null>(null);
  const { isLoading, loadingErrorMessage, app, appLoadedAt } = state;
  const mainRef = useRef<HTMLDivElement>(null);
  const { user, freePlanUsageHours } = useOutletContext<{ user: User, freePlanUsageHours: number }>();

  useEffect(
    () => {
      const subscription = back4app2.subscribeToApp(appId as string, (error, snapshot) => {
        if (error) {
          if (error instanceof NetworkError) {
            console.error('network error', error);
            dispatch(finishLoading('Network error when loading app. Check your internet connection and try again.'));
          } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
            window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
          } else if (error instanceof NotFoundError) {
            dispatch(finishLoading('App not found.'));
          } else {
            console.error('unexpected error loading app', error);
            dispatch(finishLoading('Unexpected error when loading app. Please try again.'));
          }
        } else {
          dispatch(finishLoading(undefined, snapshot));
        }        
      });

      return () => {
        subscription.unsubscribe();
        dispatch(reset());
      }
    },
    [appId]
  );

  const updateIsDeletingApp = (isDeleting: boolean) => dispatch(setIsDeletingApp(isDeleting));

  const redeployApp = async (mainServiceEnvironment: ServiceEnvironment | undefined, appId: string,) => {
    if (!app) {
      return;
    }

    if (!app.isOwner) {
      toast.error('You don\'t have permission to trigger deployments.', {
        className: TOAST_SURFACE,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      return;
    }

    if (!hasWritePermission(app, 'Deploy')) {
      toast.error('You don\'t have permission to trigger deployments.', {
        className: TOAST_SURFACE,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      return;
    }

    trackEvent(AmplitudeEvent.DEPLOY_LATEST_COMMIT);
    const toastId = toast.loading('Deploying latest commit...', {
      className: TOAST_SURFACE,
      icon: <StatusSpinnerSVG width="24px" height="24px" color="text-old-blue" className="animate-spin" />
    });
    try {
      const prepared = await runPreDeploy(
        mainServiceEnvironment?.id as string
      );

      if (prepared.blocked) {
        // The block card on the overview carries the explanation and the
        // appeal; a toast would only repeat it worse.
        toast.dismiss(toastId);
        navigate(`/apps/${appId}`);
        return;
      }

      if (prepared.needsConfirmation && prepared.runAssistance) {
        toast.dismiss(toastId);
        setPendingDeploy({
          serviceEnvironmentId: mainServiceEnvironment?.id as string,
          runAssistance: prepared.runAssistance,
        });
        return;
      }

      await back4app2.triggerManualDeployment(mainServiceEnvironment?.id as string);
      toast.success('New deployment created!', {
        className: TOAST_SURFACE,
        id: toastId,
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in top-" />
      });
      navigate(`/apps/${appId}`);
    } catch (err: any) {
      let errMsg = 'Something went wrong!';
      if (err instanceof AppStatusError || err instanceof UserVerificationPendingError || err instanceof BlockedResourceError || err instanceof PaymentPendingError || err instanceof NotFoundError || err instanceof DeploymentStatusError || err instanceof AuthorizationError) {
        errMsg = err.message;
      }
      toast.error(errMsg, {
        className: TOAST_SURFACE.replace('max-w-xs', 'min-w-xs'),
        id: toastId,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
    }
  }
  

  useEffect(() => {
    if (app?.status === AppStatus.PENDING_VERIFICATION && app?.error && app.error.code === DisposableEmailError.CODE) {
      toast.error(app.error.message, {
        className:`bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-3xl text-sm`,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in flex-none" />,
        duration: 5000
      });
    }
  }, [app])

  if (isLoading) {
    return (<>
      <div className="grow flex flex-col justify-center items-center">
        <LoadingSpinner />
        <div className="mt-4 text-ds-135 text-navy-200">
          Loading app
        </div>
      </div>
    </>);
  }

  if (loadingErrorMessage) {
    return (<>
      <ErrorMessage message={loadingErrorMessage} />
    </>);
  }

  const env = app!.mainService && app!.mainService.mainServiceEnvironment;
  const customDomain = env && env.mainCustomDomain;
  const plan = env && env.plan;
  const planRAM = plan?.maxRAM
    ? (plan.maxRAM >= 1024 ? `${plan.maxRAM / 1024} GB` : `${plan.maxRAM} MB`)
    : undefined;

  // Every destination named once. `hash` is for the two Settings tabs promoted
  // into the menu — they are sections of that screen, not routes of their own,
  // and MyAppSettings already switches on the browser hash.
  const NAV: Record<string, NavDestination> = {
    overview: { label: 'Overview', short: 'Overview', Icon: OverviewIcon, url: `/apps/${appId}` },
    logs: { label: 'Logs', short: 'Logs', Icon: LogsIcon, url: `/apps/${appId}/logs` },
    metrics: { label: 'Metrics', short: 'Metrics', Icon: MetricsIcon, url: `/apps/${appId}/metrics` },
    deployments: { label: 'Deployments', short: 'Deploys', Icon: DeploymentsIcon, url: `/apps/${appId}/deployments` },
    variables: { label: 'Env variables', short: 'Env', Icon: VariablesIcon, url: `/apps/${appId}/settings`, hash: '#variable' },
    domains: { label: 'Domains', short: 'Domains', Icon: GlobeIcon, url: `/apps/${appId}/settings`, hash: '#custom-domain' },
    settings: { label: 'Settings', short: 'Settings', Icon: SettingsIcon, url: `/apps/${appId}/settings` },
  };

  // Three questions, in the order they get asked: is it up, how do I ship to
  // it, how is it configured.
  const navGroups = [
    { eyebrow: 'monitor', items: [NAV.overview, NAV.logs, NAV.metrics] },
    { eyebrow: 'ship', items: [NAV.deployments, NAV.variables] },
    { eyebrow: 'configure', items: [NAV.domains, NAV.settings] },
  ];

  // The bottom bar keeps the five it had. Seven tabs across 360px is not a tab
  // bar, and the two promoted above are settings sections a phone still
  // reaches through Settings.
  const tabSections = [NAV.overview, NAV.deployments, NAV.logs, NAV.metrics, NAV.settings];

  // The sidebar's "All apps" is a destination — it leaves the containers
  // dashboard for the account-wide list, and says so.
  const goToAllApps = () => { window.location.href = `${BACK4APP_DOT_COM_DASHBOARD_URL}/apps`; };

  // The arrow in the mobile header is a back button, and now behaves like one.
  // React Router stamps an `idx` on history state; anything above zero means we
  // navigated here from inside the app and have somewhere of our own to return
  // to. Landing here cold — a deep link, a fresh tab — there is no previous
  // screen, so it falls back to the app list rather than dropping the person
  // out of the site.
  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;

    if (idx > 0) {
      navigate(-1);
    } else {
      navigate('/apps');
    }
  };

  // Which route built the app. The SDK asks for `sourceKind` rather than a
  // truthiness test on `repository`, but the field arrived after some apps
  // did — where it is missing, fall back to the old test.
  const isImageSource = env
    ? (env.sourceKind ? env.sourceKind === 'DOCKER_HUB' : !app!.mainService?.repository)
    : false;

  /**
   * What the app is built from, in one line under its name. The artboard gives
   * it no label: at this size the image reference is self-evidently an image
   * reference, and a rule above it would only push the status card down.
   */
  const sourceLine = env ? (isImageSource ? (
    <span className="block font-courrier text-ds-115 text-navy-300 truncate">
      {env.imageRepository}:{env.imageTag}
    </span>
  ) : (
    <span className="flex flex-col gap-1 min-w-0">
      {app!.mainService?.repository ? (
        <a
          target="_blank"
          rel="noreferrer"
          href={`https://www.github.com/${app!.mainService.repository.fullName}`}
          className="b4-focus font-courrier text-ds-115 text-navy-300 hover:text-action-soft truncate"
        >
          {app!.mainService.repository.fullName}
        </a>
      ) : null}
      <span className="flex items-center gap-1.5 min-w-0 font-courrier text-ds-115 text-navy-300">
        <BranchIcon size={12} />
        <span className="truncate">{env.branchName}</span>
      </span>
    </span>
  )) : null;

  /**
   * The address the app is served on.
   *
   * A blocked app must not advertise a live address. The URL is hidden
   * wherever it appears while the security review is open — here, in the app
   * list, and in the deploy summary on the overview.
   */
  const appUrlLine = env
    && customDomain
    && customDomain.status !== CustomDomainStatus.EXPIRED
    && !app!.block ? (
      <a
        target="_blank"
        rel="noreferrer"
        href={`${window.location.protocol}//${customDomain.name}`}
        className="b4-focus inline-flex items-center gap-1.5 min-w-0 font-courrier text-ds-115 text-state-ok-soft hover:text-white"
      >
        <span className="truncate">{customDomain.name}</span>
        <LinkIconSVG className="flex-none" />
      </a>
    ) : null;

  /**
   * The state of the app, as the one card the sidebar leads with — and the
   * handle for the Action menu, which no longer has a button of its own. The
   * chevron is drawn only when the menu has something in it.
   */
  const settingsTabs = getVisibleSettingsTabs(app);
  const isSettingsRoute = pathname.startsWith(`/apps/${appId}/settings`);
  const activeSettingsTab = isSettingsRoute
    ? (settingsTabs.find(tab => tab.id === hash.replace('#', '')) || settingsTabs[0])
    : undefined;

  /**
   * Switching sections unmounts the form, and an unmounted form's edits are
   * gone. It used to happen in silence; now it asks.
   */
  const goToSettingsTab = (id: string) => {
    if (saveState && saveState.dirtyCount > 0 && isSettingsRoute && id !== activeSettingsTab?.id) {
      setPendingSettingsTab(id);
      return;
    }
    navigate(`/apps/${appId}/settings#${id}`);
  };

  const status = getAppStatusSummary(app!);
  const lastDeployedAt = env?.lastDeployment?.createdAt;
  const canAct = canRunAppActions(app!);

  const statusCard = (
    <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-card border border-navy-500 bg-navy-700 ${
      canAct ? 'transition-colors duration-[120ms] hover:bg-navy-600' : ''
    }`}>
      <StatusDot tone={status.tone} halo />
      <span className="flex-1 min-w-0 flex flex-col gap-0.5">
        <span className="font-sora text-ds-135 font-semibold tracking-[-0.01em] text-white truncate">
          {status.text}
        </span>
        {lastDeployedAt ? (
          <span className="font-courrier text-ds-115 text-navy-300 truncate">
            {timeFromNow(lastDeployedAt)}
          </span>
        ) : null}
      </span>
      {canAct ? <ChevronRightIcon size={14} className="flex-none text-navy-300" /> : null}
    </div>
  );

  /**
   * The temporary-URL notice.
   *
   * Overview only, and nowhere else: it is an advert for a plan, and repeating
   * it above Deployments, Logs, Metrics and Settings put it on top of every
   * screen in the app. The Overview is where "is it up, and for how long?" is
   * the question being asked.
   */
  const domainStatus = customDomain?.status;
  const isFreePlan = !!plan?.name.includes('Free');
  const isOverview = pathname === `/apps/${appId}` || pathname === `/apps/${appId}/`;

  // 44px targets on a phone, the 38px control height from 1024 up.
  const CTA_SIZE = 'lg:!h-[38px]';

  const renderStatusNotice = () => {
    // "Temporary URL active" over a Blocked pill is two answers to the same
    // question. While the review is open the URL story is not the story.
    if (app!.block) {
      return null;
    }

    if (domainStatus === CustomDomainStatus.EXPIRED) {
      return (
        <SurfaceCard elevated className="p-4 lg:px-[18px] flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-6">
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            {/* Wraps rather than competing for one line: on a phone the title
                broke over two lines while the qualifier beside it was clipped
                mid-word. The dot stays welded to the title, and the qualifier
                takes the next line when there is no room. */}
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <span className="flex items-center gap-2.5">
                <StatusDot tone="warn" halo />
                <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white whitespace-nowrap">
                  URL expired
                </span>
              </span>
              <span className="min-w-0 font-courrier text-ds-115 text-navy-300 truncate">
                temporary URL on the free plan
              </span>
            </div>
            <span className="text-ds-135 leading-[1.5] text-navy-100">
              Redeploy to keep experimenting, or upgrade for a URL that stays.
            </span>
          </div>
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:flex-none">
            <DsButton
              variant="primary"
              size="touch"
              className={CTA_SIZE}
              isDisabled={!app?.isOwner}
              onClick={() => env && redeployApp(env, appId as string)}
            >
              Redeploy app
            </DsButton>
            <DsButton
              variant="action"
              size="touch"
              className={CTA_SIZE}
              onClick={() => navigate(upgradePath(appId as string))}
            >
              Upgrade for a permanent URL
            </DsButton>
          </div>
        </SurfaceCard>
      );
    }

    if (domainStatus === CustomDomainStatus.READY && isFreePlan) {
      return (
        <SurfaceCard elevated className="p-4 lg:px-[18px] flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-6">
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <span className="flex items-center gap-2.5">
                <StatusDot tone="ok" halo />
                <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white whitespace-nowrap">
                  Temporary URL active
                </span>
              </span>
              <span className="min-w-0 font-courrier text-ds-115 text-navy-300 truncate">
                live for 60 minutes
              </span>
            </div>
            <span className="text-ds-135 leading-[1.5] text-navy-100">
              This URL is temporary. Upgrade for one that stays.
            </span>
          </div>
          <DsButton
            variant="action"
            size="touch"
            className={`${CTA_SIZE} lg:flex-none`}
            onClick={() => navigate(upgradePath(appId as string))}
          >
            Upgrade for a permanent URL
          </DsButton>
        </SurfaceCard>
      );
    }

    return null;
  };

  return (<>
    <div className="flex-1 min-h-0 flex bg-navy-900">

      {/* ── Sidebar: desktop only. On mobile it becomes the context header
             plus the bottom tab bar below. ─────────────────────────────── */}
      <aside className="hidden lg:flex flex-none w-[232px] bg-navy-800 border-r border-navy-700 flex-col overflow-y-auto b4-thin">
        <div className="px-3 py-5 border-b border-navy-700 flex flex-col gap-4">
          {/* "All apps" owns its row. BETA rides with the eyebrow instead,
              where it labels the container rather than the way out. */}
          <div className="px-1">
            <button
              type="button"
              onClick={goToAllApps}
              className="b4-focus inline-flex items-center gap-1.5 rounded-control px-1 py-0.5 -ml-1 text-[13px] font-medium text-navy-200 hover:text-white transition-colors duration-[120ms]"
            >
              <ArrowLeftIcon size={14} />
              All apps
            </button>
          </div>

          <div className="px-1 flex flex-col gap-2 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <Eyebrow>container</Eyebrow>
              <StatusPill tone="info">Beta</StatusPill>
            </div>
            <div
              className="font-sora text-ds-20 font-semibold tracking-[-0.02em] leading-[1.2] text-white truncate"
              title={app!.name}
            >
              {app!.name}
            </div>
            {sourceLine}
            {appUrlLine}
          </div>

          {/* The status card is the Action menu's trigger. Where the menu has
              nothing to offer it stays a card and simply does not open. */}
          <div className="px-1">
            {canAct
              ? <MyAppActions app={app!} variant="card">{statusCard}</MyAppActions>
              : statusCard}
          </div>
        </div>

        <div className="px-2 py-4 flex flex-col gap-5">
          {navGroups.map(group => (
            <div key={group.eyebrow} className="flex flex-col gap-1">
              <div className="px-2.5 pb-1.5"><Eyebrow>{group.eyebrow}</Eyebrow></div>
              {group.items.map(item => (
                <MenuItem
                  key={`${item.url}${item.hash || ''}`}
                  Icon={item.Icon}
                  title={item.label}
                  url={item.url}
                  hash={item.hash}
                />
              ))}
            </div>
          ))}
        </div>

        {plan ? (
          <div className="mt-auto px-4 pt-4 pb-5 border-t border-navy-700 flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white">{plan.name}</span>
              <button
                type="button"
                onClick={() => navigate(upgradePath(appId as string))}
                className="b4-focus rounded-control text-ds-125 font-semibold text-action-soft hover:text-white"
              >
                Upgrade
              </button>
            </div>
            <span className="font-courrier text-ds-115 leading-[1.5] text-navy-200">
              {[planRAM, plan.maxCPU ? `${plan.maxCPU} vCPU` : undefined].filter(Boolean).join(' · ')}
            </span>
          </div>
        ) : null}
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">

        {/* ── Mobile context header ──────────────────────────────────────── */}
        {/* The arrow is a back button here, so it carries no label — it
            returns to wherever you came from, not to a fixed destination. The
            sidebar's labelled "All apps" is the other thing, and stays. */}
        {/* Sideways it collapses to one row. BETA and the CONTAINER eyebrow
            are the two things here that say nothing about this app in
            particular, so they are what a short screen spends first. */}
        <div className="lg:hidden flex-none bg-navy-800 border-b border-navy-500 px-4 pt-3 pb-4 short:py-1.5 flex items-center gap-2.5">
          <button
            type="button"
            aria-label="Go back"
            onClick={goBack}
            className="b4-focus w-11 h-11 short:w-9 short:h-9 flex-none -ml-2.5 rounded-control text-navy-200 flex items-center justify-center hover:bg-navy-600"
          >
            <ArrowLeftIcon size={18} />
          </button>
          <div className="flex-1 min-w-0 flex flex-col gap-2 short:flex-row short:items-center short:gap-2.5">
            <div className="flex items-center gap-2 short:contents">
              <span className="short:hidden"><StatusPill tone="info">Beta</StatusPill></span>
              <span className="short:hidden"><Eyebrow>container</Eyebrow></span>
              <span className="ml-auto flex-none short:order-2 short:ml-0">
                <MyAppStatus app={app!} variant="pill" />
              </span>
            </div>
            <div className="flex items-center gap-3 short:contents">
              <span className="flex-1 min-w-0 font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white truncate short:order-1">
                {app!.name}
              </span>
              <span className="flex-none short:order-3">
                <MyAppActions app={app!} compact />
              </span>
            </div>
          </div>
        </div>

        {/* pb-24 keeps the last row clear of the bottom tab bar */}
        <main
          className="flex-1 min-h-0 overflow-y-auto b4-thin px-4 pt-4 pb-24 lg:px-8 lg:pt-7 lg:pb-10 flex flex-col gap-5 lg:gap-6 [&>*]:min-w-0"
          ref={mainRef}
        >
          {isOverview ? renderStatusNotice() : null}
          <SettingsSaveProvider value={setSaveState}>
            <Outlet context={{ app, appLoadedAt, mainRef, user, freePlanUsageHours, updateIsDeletingApp, saveState, goToSettingsTab }} />
          </SettingsSaveProvider>
        </main>

        {/* ── Mobile tab bar. Five destinations, same five the sidebar has —
               nothing moves into a "More" sheet. ─────────────────────────── */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-navy-800 border-t border-navy-500 px-2 pt-1.5 pb-4 short:pt-1 short:pb-1 flex gap-0.5 [padding-bottom:calc(1rem+env(safe-area-inset-bottom))] short:[padding-bottom:calc(0.25rem+env(safe-area-inset-bottom))]">
          {tabSections.map(section => (
            <TabItem
              key={section.url}
              Icon={section.Icon}
              title={section.short}
              url={section.url}
              /* Settings names six places, not one. Tapping it shows them and
                 waits — picking one is what navigates. */
              onOpensMenu={section === NAV.settings
                ? () => setIsSectionMenuOpen(prev => !prev)
                : undefined}
            />
          ))}
        </nav>
      </div>
    </div>

    {/* Chrome of the shell, not of one screen: the section menu has to be able
        to open over Overview or Logs, where MyAppSettings is not mounted. */}
    <SettingsFooter
      saveState={saveState}
      tabs={settingsTabs.map(tab => ({ id: tab.id, label: tab.label, count: tab.count, danger: tab.danger }))}
      activeTabId={activeSettingsTab?.id ?? ''}
      onSelectTab={goToSettingsTab}
      isOpen={isSectionMenuOpen}
      onClose={() => setIsSectionMenuOpen(false)}
    />

    <ModalDialog
      open={!!pendingSettingsTab}
      onClose={() => setPendingSettingsTab(null)}
      icon={<TriangleExclamationIcon className="text-state-err" width="20px" height="20px" />}
      title="Unsaved changes"
      blurb={`You have ${saveState?.dirtyCount ?? 0} unsaved change${saveState?.dirtyCount === 1 ? '' : 's'} in this section. Leaving discards ${saveState?.dirtyCount === 1 ? 'it' : 'them'}.`}
      footer={<>
        <DsButton onClick={() => setPendingSettingsTab(null)}>Stay here</DsButton>
        <DsButton
          variant="danger"
          onClick={() => {
            const target = pendingSettingsTab;
            setPendingSettingsTab(null);
            if (target) {
              navigate(`/apps/${appId}/settings#${target}`);
            }
          }}
        >
          Discard and leave
        </DsButton>
      </>}
    />
    {pendingDeploy && app?.mainService?.mainServiceEnvironment ? (
      <PreDeployConfirmModal
        appId={appId as string}
        serviceEnvironment={app.mainService.mainServiceEnvironment}
        runAssistance={pendingDeploy.runAssistance}
        onCancel={() => setPendingDeploy(null)}
        onProceed={async () => {
          const { serviceEnvironmentId } = pendingDeploy;
          setPendingDeploy(null);
          await back4app2.triggerManualDeployment(serviceEnvironmentId);
          navigate(`/apps/${appId}`);
        }}
      />
    ) : null}
  </>);
};

/** A row of the menu. `short` is the label the mobile tab bar has room for. */
interface NavDestination {
  label: string;
  short: string;
  Icon: (props: { size?: number, className?: string }) => JSX.Element;
  url: string;
  hash?: string;
}

interface NavItemProps {
  Icon: (props: { size?: number, className?: string }) => JSX.Element;
  title: string;
  url: string;
  /** Set for the Settings sections that have their own row in the menu. */
  hash?: string;
}

/**
 * The Settings tabs promoted into the menu share Settings' path, so the hash
 * is what tells the three rows apart. Settings itself is the one without a
 * hash, and must go dark while either of the others is showing.
 */
const PROMOTED_SETTINGS_HASHES = ['#variable', '#custom-domain'];

/** Overview is the index route, so it only matches exactly. */
const useIsActive = (url: string, hash?: string) => {
  const { pathname, hash: currentHash } = useLocation();
  const onPath = pathname === url || pathname === `${url}/`;

  if (!onPath) {
    return false;
  }
  if (hash) {
    return currentHash === hash;
  }
  return !PROMOTED_SETTINGS_HASHES.includes(currentHash);
};

const MenuItem = ({ Icon, title, url, hash }: NavItemProps) => {
  const isActive = useIsActive(url, hash);

  return (
    <Link
      to={`${url}${hash || ''}`}
      className={[
        'b4-focus flex items-center gap-2.5 h-[38px] px-2.5 rounded-control text-ds-135',
        'transition-colors duration-[120ms]',
        isActive ? 'bg-navy-600 text-white font-semibold' : 'text-navy-200 font-medium hover:text-white hover:bg-navy-600/50',
      ].join(' ')}
    >
      <Icon size={16} />
      {title}
    </Link>
  );
};

const TabItem = ({ Icon, title, url, onOpensMenu }: NavItemProps & { onOpensMenu?: () => void }) => {
  const isActive = useIsActive(url);

  return (
    <Link
      to={url}
      aria-haspopup={onOpensMenu ? 'menu' : undefined}
      onClick={onOpensMenu ? (event) => { event.preventDefault(); onOpensMenu(); } : undefined}
      data-settings-menu-trigger={onOpensMenu ? '' : undefined}
      className={[
        'b4-focus flex-1 min-h-[52px] flex flex-col items-center justify-center gap-1 py-1.5 rounded-surface',
        // Sideways the label moves next to the icon instead of under it, which
        // is the whole difference between a 52px bar and a 36px one.
        'short:min-h-[36px] short:flex-row short:gap-1.5 short:py-0.5',
        'uppercase text-ds-105 font-semibold tracking-[0.04em] transition-colors duration-[120ms]',
        isActive ? 'bg-navy-600 text-white' : 'text-navy-300',
      ].join(' ')}
    >
      <Icon size={18} className="short:w-4 short:h-4" />
      {title}
    </Link>
  );
};

export default MyApp;
